// Justimmo-Anbindung — der Zustand der Schnittstelle in Sätzen.
//
// Der wichtigste Zustand dieser Seite ist der von heute: NICHTS ist
// eingerichtet, kein Aufruf hat je stattgefunden, und `/justimmo/status`
// existiert in der laufenden API-Version noch gar nicht. Genau dieser Stand
// muss sich gut lesen — sonst ist die Seite eine Attrappe, die erst dann
// etwas taugt, wenn man sie ohnehin nicht mehr braucht.
//
// Drei Regeln, alle drei aus demselben Vorfall:
//   * Kein blankes Ja/Nein und kein blanker Statuscode. Ein Haken ohne Satz
//     ist der Haken, der am 24.08. fünfzehn Tage lang über 243 gescheiterten
//     SMS stand — korrekt („das Secret ist gesetzt") und trotzdem nutzlos.
//   * Anwesenheit heisst nie „funktioniert". Der Satz steht auf der Seite,
//     nicht nur im Kommentar.
//   * „Nicht feststellbar" ist ein eigener Zustand und nie dasselbe wie
//     „nichts vorhanden" — eine leere Liste und eine fehlende Liste dürfen
//     nicht gleich aussehen (die Lektion aus Batch BR).
//
// Nur Administration, in `beforeLoad` und im Nav-Eintrag mit derselben
// Bedingung. Die Seite löst NICHTS aus: sie liest, mehr nicht.

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  HelpCircle,
  Plug,
  RefreshCw,
  Server,
  Webhook,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ListingProposals } from '@/components/admin/ListingProposals';
import { fetchMe } from '@/lib/auth';
import { formatDateTime, formatNumber } from '@/lib/formatLocale';
import {
  JUSTIMMO_TENANT,
  JUSTIMMO_WEBHOOK_ENDPOINT,
  JUSTIMMO_WEBHOOK_VAR,
  deriveCredentialState,
  deriveWebhookState,
  toMs,
  useJustimmoHealth,
  useJustimmoStatus,
  type CredentialState,
  type JustimmoBlame,
  type JustimmoRunRow,
  type JustimmoTarget,
  type WebhookState,
} from '@/hooks/data/useJustimmo';

export const Route = createFileRoute('/_app/admin/justimmo')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (me?.profile?.role !== 'admin') throw redirect({ to: '/dashboard' });
  },
  component: JustimmoPage,
});

const TARGETS: JustimmoTarget[] = ['production', 'sandbox'];

/** Die deutschen Sätze je Befund — zugleich der Rückfall für `t()`, damit ein
 *  neuer Code nie als nackter Schlüssel auf dem Bildschirm landet. Die
 *  Schlüssel sind dieselben Slugs, die `services/justimmo/diagnose.ts`
 *  serverseitig ausgibt. */
const DIAG: Record<string, { title: string; next: string }> = {
  credentials_present: {
    title: '{{target}}: alle drei Variablen sind gesetzt.',
    next: 'Gesetzt heisst nicht erreichbar. Ob der Zugang trägt, beantwortet erst ein echter Aufruf.',
  },
  credentials_missing: {
    title: '{{target}}: noch nicht eingerichtet.',
    next: 'Es fehlt: {{missing}}. Solange eine dieser Variablen fehlt, kommt kein einziger Aufruf bei Justimmo an.',
  },
  credentials_aggregate_complete: {
    title: '{{target}}: laut Sammelantwort vollständig eingerichtet.',
    next: 'Die laufende API-Version meldet die Variablen nicht einzeln, sondern nur zusammen. Welche gesetzt ist, ist von hier aus erst nach dem nächsten API-Deploy zu sehen.',
  },
  credentials_aggregate_incomplete: {
    title: '{{target}}: laut Sammelantwort nicht vollständig eingerichtet.',
    next: 'Die laufende API-Version meldet die Variablen nur zusammen — welche der drei fehlt, sagt sie nicht. Nach dem nächsten API-Deploy steht hier der Name.',
  },
  credentials_unknown_api_version: {
    title: '{{target}}: nicht feststellbar.',
    next: 'Die laufende API-Version prüft diese Variablen gar nicht. Das ist keine Aussage über die Einrichtung — es ist eine Lücke in der Diagnose, die der nächste API-Deploy schliesst.',
  },
  webhook_deliveries_unknown: {
    title: '{{target}}: über eingegangene Zustellungen ist nichts bekannt.',
    next: 'Die laufende API-Version kennt die Abfrage dazu noch nicht. Ob Justimmo den Webhook freigeschaltet hat, ist von hier aus deshalb weder bestätigt noch widerlegt.',
  },
  webhook_not_configured: {
    title: '{{target}}: kein Secret gesetzt und noch nie eine Zustellung angekommen.',
    next: 'Beides steht aus. Justimmo muss den Webhook für diesen Mandanten freischalten und dabei das Secret ausgeben — es wird genau einmal vergeben und lässt sich nicht vorab hinterlegen.',
  },
  webhook_never_delivered: {
    title: '{{target}}: Secret liegt vor, aber es ist noch nie eine Zustellung angekommen.',
    next: 'Das ist der Stand „nie freigeschaltet“. Das ist eine Mail an Justimmo, keine Codeänderung — GET /webhooks zeigt, welche Registrierungen dieser Mandant hat.',
  },
  webhook_secret_missing: {
    title: '{{target}}: Zustellungen kommen an, werden aber nur roh gespeichert.',
    next: 'Ohne Secret kann keine Zustellung als echt erkannt und damit keine verarbeitet werden. Nichts geht verloren — sie warten in der Tabelle.',
  },
  webhook_signature_all_bad: {
    title: '{{target}}: keine einzige Zustellung hat die Signaturprüfung bestanden.',
    next: 'Ein falsches Secret oder eine andere Kodierung als Hex. Das Secret gehört genau einem Webhook — Produktion und Sandbox haben verschiedene.',
  },
  webhook_signature_some_bad: {
    title: '{{target}}: ein Teil der Zustellungen hat keine gültige Signatur.',
    next: 'Gemischt — typischerweise ein gewechseltes Secret oder ein zweiter Webhook auf denselben Endpunkt. Die ungeprüften Zeilen bleiben liegen, sie werden nicht verarbeitet.',
  },
  webhook_parser: {
    title: '{{target}}: echte Zustellungen, aus denen wir keine Kennung gelesen haben.',
    next: 'Unser Code, nicht Justimmo. Die Rohdaten liegen vollständig in der Tabelle — nach einer Korrektur ist der Rückstand nachträglich lesbar.',
  },
  webhook_ok: {
    title: '{{target}}: alle Zustellungen geprüft und gelesen.',
    next: 'Gespeichert heisst in dieser Phase noch nicht angewendet — verarbeitet wird erst später.',
  },
};

function JustimmoPage() {
  const { t } = useTranslation();
  const health = useJustimmoHealth();
  const status = useJustimmoStatus();

  const reload = () => {
    void health.refetch();
    void status.refetch();
  };

  const busy = health.isFetching || status.isFetching;

  return (
    <section className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('justimmo.title', 'Justimmo-Anbindung')}
          </h2>
          <p className="max-w-3xl text-sm text-ink-3">
            {t(
              'justimmo.subtitle',
              'Zugangsdaten, Webhook und die letzten Durchgänge — je Mandant getrennt. Diese Seite liest nur; sie löst nichts aus.',
            )}
          </p>
        </div>
        <Button type="button" variant="outline" onClick={reload} disabled={busy}>
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          {busy ? t('justimmo.reloading', 'Wird gelesen …') : t('justimmo.reload', 'Neu laden')}
        </Button>
      </header>

      <PresenceNote />

      {health.isError && (
        <div className="fm-card border-destructive/40 p-4">
          <p className="text-sm font-medium text-ink">
            {t(
              'justimmo.health_failed.title',
              'Der Diagnose-Endpunkt der eigenen API war nicht erreichbar.',
            )}
          </p>
          <p className="mt-1 text-sm text-ink-3">
            {t(
              'justimmo.health_failed.next',
              'Alles unten ist damit unbekannt — und das ist ausdrücklich keine Aussage über Justimmo. Bitte neu laden.',
            )}
          </p>
        </div>
      )}

      <section className="space-y-3">
        <SectionHead
          icon={<Server className="h-4 w-4" />}
          title={t('justimmo.credentials.heading', 'Zugangsdaten')}
          intro={t(
            'justimmo.credentials.intro',
            'Je Mandant drei Worker-Variablen. Produktion und Sandbox haben eigene: ein Zugang, der auf die Zugangsdaten des anderen Mandanten zeigt, meldet sich sauber an und liest die falschen Objekte.',
          )}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {TARGETS.map((target) => (
            <CredentialCard
              key={target}
              state={deriveCredentialState(health.data, target)}
              loading={health.isLoading}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <SectionHead
          icon={<Webhook className="h-4 w-4" />}
          title={t('justimmo.webhook.heading', 'Webhook')}
          intro={t(
            'justimmo.webhook.intro',
            'Justimmo hat für Immobilien keine Abfrage nach Änderungsdatum — der Webhook ist die einzige Möglichkeit, von einer Änderung zu erfahren, ohne alles neu zu lesen. Freischalten kann ihn nur Justimmo.',
          )}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {TARGETS.map((target) => (
            <WebhookCard
              key={target}
              state={deriveWebhookState(health.data, status.data, target)}
              loading={health.isLoading || status.isLoading}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <SectionHead
          icon={<Plug className="h-4 w-4" />}
          title={t('justimmo.runs.heading', 'Letzte Durchgänge')}
          intro={t(
            'justimmo.runs.intro',
            'Jeder Durchgang schreibt seine Zeile, bevor er beginnt. Ein Durchgang, der nichts gefunden hat, muss von einem zu unterscheiden sein, der nie stattgefunden hat.',
          )}
        />
        <RunsPanel />
      </section>

      <section className="space-y-3">
        <SectionHead
          icon={<Plug className="h-4 w-4" />}
          title={t('listing_proposals.heading', 'Inserate der Website')}
          intro={t(
            'listing_proposals.intro',
            'Der Abgleich liest den Bestand der Website und verbindet eindeutige Inserate mit Objekten im CRM. Alles Unklare steht hier zur Entscheidung.',
          )}
        />
        <ListingProposals />
      </section>
    </section>
  );
}

/** Der Satz, den diese Seite ihrem eigenen Anspruch schuldet. Er steht oben und
 *  nicht im Kleingedruckten, weil der Vorfall, aus dem er stammt, genau davon
 *  lebte, dass niemand nachgesehen hat. */
function PresenceNote() {
  const { t } = useTranslation();
  return (
    <div className="fm-card border-s-4 border-s-[hsl(var(--brand-gold))] p-4">
      <p className="font-display text-base text-ink">
        {t('justimmo.presence_note.title', 'Gesetzt heisst nicht erreichbar.')}
      </p>
      <p className="mt-1 max-w-4xl text-sm text-ink-2">
        {t(
          'justimmo.presence_note.body',
          'Diese Seite kann sagen, welche Variablen hinterlegt sind und was bisher angekommen ist. Ob ein Aufruf bei Justimmo wirklich durchgeht, beantwortet erst ein echter Aufruf. Am 24. August stand für den SMS-Versand fünfzehn Tage lang ein grüner Haken, während 243 Nachrichten scheiterten: das Secret war gesetzt, der Versand war tot. Ein Haken ohne Satz ist genau dieser Haken.',
        )}
      </p>
    </div>
  );
}

function SectionHead({
  icon,
  title,
  intro,
}: {
  icon: React.ReactNode;
  title: string;
  intro: string;
}) {
  return (
    <div>
      <h3 className="flex items-center gap-2 font-display text-lg text-ink">
        <span className="text-ink-3" aria-hidden>
          {icon}
        </span>
        {title}
      </h3>
      <p className="max-w-4xl text-sm text-ink-3">{intro}</p>
    </div>
  );
}

/** Wer am Zug ist — als Wort, nie als Farbe allein. */
function BlamePill({ blame }: { blame: JustimmoBlame }) {
  const { t } = useTranslation();
  const map: Record<JustimmoBlame, { cls: string; label: string }> = {
    setup: { cls: 'fm-pill-warn', label: t('justimmo.blame.setup', 'Einrichtung bei uns') },
    us: { cls: 'fm-pill-dgr', label: t('justimmo.blame.us', 'Unser Code') },
    justimmo: { cls: 'fm-pill-info', label: t('justimmo.blame.justimmo', 'Justimmo') },
    infra: { cls: 'fm-pill-info', label: t('justimmo.blame.infra', 'Infrastruktur') },
    none: { cls: 'fm-pill-pos', label: t('justimmo.blame.none', 'Nichts zu tun') },
    unknown: {
      cls: 'bg-paper-3 text-ink-3',
      label: t('justimmo.blame.unknown', 'Nicht feststellbar'),
    },
  };
  const v = map[blame];
  return <span className={`fm-pill ${v.cls}`}>{v.label}</span>;
}

/** Titel + nächster Schritt. Nie nur der Titel: „was ist" ohne „was jetzt"
 *  schickt die Person zurück in die Suche. */
function Verdict({
  code,
  vars,
  serverTitle,
  serverNext,
}: {
  code: string;
  vars?: Record<string, string>;
  serverTitle?: string;
  serverNext?: string;
}) {
  const { t } = useTranslation();
  const fallback = DIAG[code];
  const title = t(`justimmo.diag.${code}.title`, fallback?.title ?? serverTitle ?? code, vars);
  // `t(key, '')` fiele auf den Schlüssel zurück, weil i18next einen leeren
  // Vorgabewert als „keiner" liest — dann stünde ein Slug auf dem Bildschirm.
  const fallbackNext = fallback?.next ?? serverNext;
  const next = fallbackNext ? t(`justimmo.diag.${code}.next`, fallbackNext, vars) : '';
  return (
    <div>
      <p className="text-sm font-medium leading-snug text-ink">{title}</p>
      {next && <p className="mt-1 text-sm leading-snug text-ink-3">{next}</p>}
    </div>
  );
}

/** Ein Zustand als WORT plus Punkt. Der Punkt allein wäre wieder ein blankes
 *  Ja/Nein, und Farbe ist für Rot-Grün-Blindheit ohnehin keine Information. */
function StateWord({ set }: { set: boolean | null }) {
  const { t } = useTranslation();
  if (set === true)
    return (
      <span className="inline-flex items-center gap-1.5 text-[hsl(var(--positive))]">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {t('justimmo.var.set', 'gesetzt')}
      </span>
    );
  if (set === false)
    return (
      <span className="inline-flex items-center gap-1.5 text-[hsl(var(--warning))]">
        <CircleDashed className="h-3.5 w-3.5" />
        {t('justimmo.var.missing', 'fehlt')}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 text-ink-3">
      <HelpCircle className="h-3.5 w-3.5" />
      {t('justimmo.var.unknown', 'nicht feststellbar')}
    </span>
  );
}

function TargetLabel({ target }: { target: JustimmoTarget }) {
  const { t } = useTranslation();
  return (
    <>
      {target === 'production'
        ? t('justimmo.target.production', 'Produktion (Mandant {{tenant}})', {
            tenant: JUSTIMMO_TENANT.production,
          })
        : t('justimmo.target.sandbox', 'Sandbox (Mandant {{tenant}})', {
            tenant: JUSTIMMO_TENANT.sandbox,
          })}
    </>
  );
}

type TFn = ReturnType<typeof useTranslation>['t'];

function targetText(target: JustimmoTarget, t: TFn): string {
  return target === 'production'
    ? t('justimmo.target.production', 'Produktion (Mandant {{tenant}})', {
        tenant: JUSTIMMO_TENANT.production,
      })
    : t('justimmo.target.sandbox', 'Sandbox (Mandant {{tenant}})', {
        tenant: JUSTIMMO_TENANT.sandbox,
      });
}

function CredentialCard({
  state,
  loading,
}: {
  state: CredentialState;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const target = targetText(state.target, t);

  return (
    <article className="fm-card min-w-0 space-y-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <h4 className="min-w-0 font-display text-base text-ink">
          <TargetLabel target={state.target} />
        </h4>
        {!loading && <BlamePill blame={state.blame} />}
      </div>

      {loading ? (
        <SkeletonLines />
      ) : (
        <>
          <Verdict
            code={state.code}
            vars={{ target, missing: state.missing.join(', ') }}
          />
          <ul className="min-w-0 space-y-1 border-t border-line pt-3">
            {state.vars.map((v) => (
              <li key={v.env} className="flex min-w-0 items-center justify-between gap-3 text-sm">
                <code className="break-all font-mono text-xs text-ink-2">{v.env}</code>
                <StateWord set={v.set} />
              </li>
            ))}
          </ul>
          {state.missing.length > 0 && (
            <div className="min-w-0 rounded-[6px] bg-paper-2 p-3">
              <p className="text-xs text-ink-3">
                {t(
                  'justimmo.credentials.command_hint',
                  'Je Variable ein Aufruf. Den Wert erst an der Eingabeaufforderung eintippen — in der Kommandozeile landet er in der Shell-Historie.',
                )}
              </p>
              <code className="mt-2 block overflow-x-auto whitespace-pre font-mono text-xs text-ink-2">
                {state.missing
                  .map((m) => `npx wrangler secret put ${m} --env staging`)
                  .join('\n')}
              </code>
            </div>
          )}
        </>
      )}
    </article>
  );
}

function WebhookCard({ state, loading }: { state: WebhookState; loading: boolean }) {
  const { t } = useTranslation();
  const target = targetText(state.target, t);
  const known = state.rows != null;

  return (
    <article className="fm-card min-w-0 space-y-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <h4 className="min-w-0 font-display text-base text-ink">
          <TargetLabel target={state.target} />
        </h4>
        {!loading && <BlamePill blame={state.blame} />}
      </div>

      {loading ? (
        <SkeletonLines />
      ) : (
        <>
          <Verdict
            code={state.server?.code ?? state.code}
            vars={{ target }}
            serverTitle={state.server?.title}
            serverNext={state.server?.next}
          />

          <dl className="min-w-0 space-y-1 border-t border-line pt-3 text-sm">
            <Row label={t('justimmo.webhook.endpoint', 'Endpunkt')} stack>
              <code className="break-all font-mono text-xs text-ink-2">
                {JUSTIMMO_WEBHOOK_ENDPOINT[state.target]}
              </code>
            </Row>
            <Row
              label={
                <code className="break-all font-mono text-xs text-ink-2">
                  {JUSTIMMO_WEBHOOK_VAR[state.target]}
                </code>
              }
            >
              <StateWord set={state.secretConfigured} />
            </Row>
            {known ? (
              <>
                <Row label={t('justimmo.webhook.rows', 'Zustellungen gespeichert')}>
                  <span className="text-ink-2">{formatNumber(state.rows ?? 0)}</span>
                </Row>
                <Row label={t('justimmo.webhook.unsigned', 'davon ohne gültige Signatur')}>
                  <span
                    className={
                      (state.unsignedRows ?? 0) > 0 ? 'text-[hsl(var(--warning))]' : 'text-ink-2'
                    }
                  >
                    {formatNumber(state.unsignedRows ?? 0)}
                  </span>
                </Row>
                <Row label={t('justimmo.webhook.no_entity', 'davon ohne erkannte Kennung')}>
                  <span
                    className={
                      (state.rowsMissingEntityId ?? 0) > 0
                        ? 'text-[hsl(var(--danger))]'
                        : 'text-ink-2'
                    }
                  >
                    {formatNumber(state.rowsMissingEntityId ?? 0)}
                  </span>
                </Row>
                <Row label={t('justimmo.webhook.last', 'Zuletzt angekommen')}>
                  <span className="text-ink-2">
                    {state.lastReceivedAt != null
                      ? formatDateTime(new Date(state.lastReceivedAt))
                      : t('justimmo.webhook.never', 'noch nie')}
                  </span>
                </Row>
              </>
            ) : (
              // Ein <p> wäre hier ungültig: in einer <dl> sind nur dt, dd und
              // div erlaubt.
              <div className="pt-1 text-sm text-ink-3">
                {t(
                  'justimmo.webhook.counts_unknown',
                  'Zustellungen, Signaturen und gelesene Kennungen: darüber liegt noch keine Auskunft vor. Nicht „null“ — unbekannt.',
                )}
              </div>
            )}
          </dl>
        </>
      )}
    </article>
  );
}

function Row({
  label,
  stack,
  children,
}: {
  label: React.ReactNode;
  /** Für Werte ohne Trennstellen (eine URL). In einer Zeile nebeneinander
   *  frisst die umbrechende Adresse sonst die Beschriftung auf — auf einem
   *  Telefon blieb von „Endpunkt" nur „Endpun". */
  stack?: boolean;
  children: React.ReactNode;
}) {
  if (stack) {
    return (
      <div className="min-w-0">
        <dt className="text-ink-3">{label}</dt>
        <dd className="min-w-0">{children}</dd>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <dt className="min-w-0 text-ink-3">{label}</dt>
      <dd className="min-w-0 text-end">{children}</dd>
    </div>
  );
}

function SkeletonLines() {
  return (
    <div className="space-y-2" aria-hidden>
      <div className="h-3 w-3/4 rounded bg-paper-3" />
      <div className="h-3 w-full rounded bg-paper-3" />
      <div className="h-3 w-1/2 rounded bg-paper-3" />
    </div>
  );
}

const RUN_STATUS: Record<string, { key: string; de: string; cls: string }> = {
  running: { key: 'justimmo.run.status.running', de: 'Läuft', cls: 'fm-pill-info' },
  ok: { key: 'justimmo.run.status.ok', de: 'Erfolgreich', cls: 'fm-pill-pos' },
  partial: { key: 'justimmo.run.status.partial', de: 'Teilweise', cls: 'fm-pill-warn' },
  failed: { key: 'justimmo.run.status.failed', de: 'Fehlgeschlagen', cls: 'fm-pill-dgr' },
};

function RunsPanel() {
  const { t } = useTranslation();
  const status = useJustimmoStatus();

  if (status.isLoading) {
    return (
      <div className="fm-card p-4">
        <SkeletonLines />
      </div>
    );
  }

  // Drei verschiedene Sachverhalte, drei verschiedene Sätze. Sie hier zu einer
  // leeren Tabelle zusammenzuziehen wäre exakt der Fehler, der einer Maklerin
  // im August eine volle Datenbank als gelöscht angezeigt hat.
  if (!status.data?.available) {
    const reason = status.data?.reason ?? 'error';
    return (
      <div className="fm-card space-y-1 p-4">
        <p className="text-sm font-medium text-ink">
          {reason === 'not_deployed'
            ? t(
                'justimmo.runs.unavailable.title',
                'Die laufende API-Version kennt diese Abfrage noch nicht.',
              )
            : reason === 'forbidden'
              ? t(
                  'justimmo.runs.forbidden.title',
                  'Diese Abfrage ist für Ihr Konto gesperrt.',
                )
              : t(
                  'justimmo.runs.error.title',
                  'Die Abfrage der Durchgänge ist fehlgeschlagen.',
                )}
        </p>
        <p className="max-w-4xl text-sm text-ink-3">
          {reason === 'not_deployed'
            ? t(
                'justimmo.runs.unavailable.next',
                'Das heisst nicht, dass es keine Durchgänge gab — es heisst, dass hier noch niemand nachsehen kann. Die Auslöser kommen mit dem nächsten API-Deploy.',
              )
            : reason === 'forbidden'
              ? t(
                  'justimmo.runs.forbidden.next',
                  'Die Durchgänge sind der Administration vorbehalten. Über den Zustand der Anbindung sagt das nichts.',
                )
              : t(
                  'justimmo.runs.error.next',
                  'Ein Fehler auf unserer Seite, kein Befund über Justimmo. Bitte neu laden; bleibt es dabei, gehört es in die Fehlermeldung.',
                )}
        </p>
      </div>
    );
  }

  const runs = status.data.data.runs ?? [];
  if (runs.length === 0) {
    return (
      <div className="fm-card space-y-1 p-4">
        <p className="text-sm font-medium text-ink">
          {t('justimmo.runs.none.title', 'Es hat noch kein Durchgang stattgefunden.')}
        </p>
        <p className="max-w-4xl text-sm text-ink-3">
          {t(
            'justimmo.runs.none.next',
            'Kein Ausfall — es wurde schlicht noch nie gelesen. Der erste Durchgang schreibt seine Zeile, bevor er beginnt, und erscheint deshalb auch dann hier, wenn er scheitert.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="fm-card overflow-hidden p-0">
      <div className="fm-table-wrap">
        <table className="w-full text-start text-sm">
          <thead>
            <tr className="border-b border-line text-[11px] uppercase tracking-[0.06em] text-ink-3">
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.when', 'Zeitpunkt')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.kind', 'Durchgang')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.trigger', 'Ausgelöst durch')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.result', 'Ergebnis')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.duration', 'Dauer')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.seen', 'Gelesen')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.created', 'Neu')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.updated', 'Aktualisiert')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.skipped', 'Übersprungen')}</th>
              <th className="px-3 py-2 text-start">{t('justimmo.run.col.failed', 'Fehler')}</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <RunRow key={r.id} run={r} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunRow({ run }: { run: JustimmoRunRow }) {
  const { t } = useTranslation();
  const started = toMs(run.started_at);
  const s = RUN_STATUS[run.status];
  const trigger =
    run.trigger === 'cron'
      ? t('justimmo.run.trigger.cron', 'Zeitplan')
      : run.trigger === 'manual'
        ? t('justimmo.run.trigger.manual', 'Von Hand')
        : run.trigger === 'webhook'
          ? t('justimmo.run.trigger.webhook', 'Webhook')
          : '—';
  const num = (v: number | null | undefined) => (v == null ? '—' : formatNumber(v));

  return (
    <>
      <tr className="border-b border-line last:border-b-0">
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">
          {started != null ? formatDateTime(new Date(started)) : '—'}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-ink">
          {run.kind}
          {run.target ? <span className="text-ink-3"> · {run.target}</span> : null}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">{trigger}</td>
        <td className="whitespace-nowrap px-3 py-2">
          <span className={`fm-pill ${s?.cls ?? 'bg-paper-3 text-ink-3'}`}>
            {s
              ? t(s.key, s.de)
              : t('justimmo.run.status.unknown', 'Unbekanntes Ergebnis')}
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">
          {run.duration_ms != null
            ? t('justimmo.run.seconds', '{{n}} s', {
                // Durch den Formatierer, nicht über toFixed: „14.3 s" ist ein
                // englisches Dezimalzeichen auf einer deutschen Oberfläche.
                n: formatNumber(run.duration_ms / 1000, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }),
              })
            : '—'}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">{num(run.seen)}</td>
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">{num(run.created)}</td>
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">{num(run.updated)}</td>
        <td className="whitespace-nowrap px-3 py-2 text-ink-2">{num(run.skipped)}</td>
        <td className="whitespace-nowrap px-3 py-2">
          <span className={(run.failed ?? 0) > 0 ? 'text-[hsl(var(--danger))]' : 'text-ink-2'}>
            {num(run.failed)}
          </span>
        </td>
      </tr>
      {run.error && (
        // Die Meldung steht ausgeschrieben da und nicht hinter einem Aufklappen:
        // ein Fehler, den man erst suchen muss, wird nicht gelesen.
        <tr className="border-b border-line last:border-b-0">
          <td colSpan={10} className="px-3 pb-3 pt-0">
            <p className="flex items-start gap-2 text-xs text-[hsl(var(--danger))]">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="font-mono break-all">{run.error}</span>
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
